// frontend/src/pages/storeSettingsForms.js
//
// Turn a `/auth/stores/settings/` payload into the per-section form
// objects StoreSettings.jsx edits; the install setup step
// (components/entry/StoreSetup.jsx) reads its planning and sourcing blocks
// — the same blocks, served by `/api/store/entry/` — through the same
// readers. Pure — no React — so the contract with the backend is
// unit-testable on its own (same shape as itemSetupRow.js).
//
// ── Why every read here is strict ────────────────────────────────────────
//
// No default substitution anywhere below. Every field these read is NOT NULL
// on the model with a DB-level default (core/models.py: timezone, language,
// region_code, unit_system, weight_unit, dimension_unit, planning_horizon_days,
// inventory_target_mode, target_days_of_supply, lead_time_days,
// review_frequency, po_pdf_show_*, po_export_show_prices_on_drafts, company_*,
// Organization.notifications_to), so
// `_get_settings` always sends a real value. `planning.preferred_order_day` is the ONE nullable
// column here, and its null is a value rather than an absence — see
// `requireSection`. A `|| 'UTC'` or `?? 90` here could therefore only fire
// when the payload contract broke — and would then show the merchant a setting
// they never chose, which the section's own Save button writes straight back
// over the real one.
//
// Substituting a timezone is the worst of these: every planning date the store
// computes shifts, silently, and the merchant has no way to see that a default
// was invented for them.
//
// A malformed section throws, and the caller's existing catch turns it into
// the page-level error.

import { PLANNING_SETTINGS_FIELDS } from '../constants/planning';

// `nullable` names the keys whose NULL is a real value rather than an absent
// one — today only `planning.preferred_order_day`, which is null exactly when
// the cadence is DAILY. A missing KEY is still a broken contract for those:
// "the store has no order day" and "the endpoint stopped sending the field"
// must not arrive as the same thing, or a DAILY store's cadence controls would
// render off a value nobody sent.
const requireSection = (section, keys, label, nullable = []) => {
  if (!section || typeof section !== 'object') {
    throw new Error(`Store settings payload is missing the "${label}" section`);
  }
  const missing = keys.filter((k) => (
    section[k] === undefined || (section[k] === null && !nullable.includes(k))
  ));
  if (missing.length > 0) {
    throw new Error(`Store settings "${label}" section is missing: ${missing.join(', ')}`);
  }
  return section;
};

const pick = (source, keys) => Object.fromEntries(keys.map((k) => [k, source[k]]));

export const ORG_FIELDS = ['name', 'notifications_to'];
// What this store's PO PDF prints. `logo_url` rides in the same section but is
// changed through its own upload endpoint, not this form.
export const COMPANY_FIELDS = [
  'name', 'address_line1', 'address_line2', 'city', 'state_province',
  'postal_code', 'phone', 'email',
];
export const STORE_FIELDS = ['name', 'contact_email', 'currency'];
export const LOCALE_FIELDS = ['language', 'region_code', 'timezone'];
export const UNIT_FIELDS = ['unit_system', 'weight_unit', 'dimension_unit'];
// The planning block is read straight off the SHARED wire contract
// (PLANNING_SETTINGS_FIELDS) rather than restated here: the whole slice — the
// forward window, the early-alert opt-out, and the roots of the chains that
// cascade by reference (inventory target pair, lead time, order cadence pair)
// — is what the Planning card edits and sends.
//
// See `requireSection`: null is the stored value under a DAILY cadence.
const PLANNING_NULLABLE_FIELDS = ['preferred_order_day'];
export const PO_FIELDS = [
  'show_line_number', 'show_supplier_code', 'show_sku', 'show_prices_on_drafts',
  'show_tax',
];
// The Exports card's own switches; its column choices are the
// ExportColumnsDialog's, saved through their own endpoint.
export const EXPORT_FIELDS = ['show_po_prices_on_drafts'];
// Where suppliers and item→supplier links come from ('manual' | 'per_vendor').
// NOT NULL with a model default, so the strict read applies.
export const SOURCING_FIELDS = ['supplier_source'];

export const extractOrgForm = (org) =>
  pick(requireSection(org, ORG_FIELDS, 'organization'), ORG_FIELDS);

export const extractCompanyForm = (company) =>
  pick(requireSection(company, COMPANY_FIELDS, 'company'), COMPANY_FIELDS);

export const extractStoreForm = (store) =>
  pick(requireSection(store, STORE_FIELDS, 'store'), STORE_FIELDS);

// Locale carries per-store language (BCP 47) + region_code (ISO 3166-1
// alpha-2) + timezone. Number/date/time formats are derived from the
// language+region tag via Intl.* at render time, so they are NOT stored or
// edited separately. `storeUnits` carries per-store weight/dimension/system
// units.
export const extractPreferencesForm = (storeUnits, locale) => ({
  ...pick(requireSection(locale, LOCALE_FIELDS, 'locale'), LOCALE_FIELDS),
  ...pick(requireSection(storeUnits, UNIT_FIELDS, 'store_units'), UNIT_FIELDS),
});

export const extractPlanningForm = (planning) => pick(
  requireSection(planning, PLANNING_SETTINGS_FIELDS, 'planning', PLANNING_NULLABLE_FIELDS),
  PLANNING_SETTINGS_FIELDS,
);

export const extractPOForm = (po) =>
  pick(requireSection(po, PO_FIELDS, 'purchase_orders'), PO_FIELDS);

export const extractExportsForm = (exports) =>
  pick(requireSection(exports, EXPORT_FIELDS, 'exports'), EXPORT_FIELDS);

export const extractSourcingForm = (sourcing) =>
  pick(requireSection(sourcing, SOURCING_FIELDS, 'sourcing'), SOURCING_FIELDS);

/**
 * Every form section in one go, so a malformed payload throws BEFORE the caller
 * writes any state — the page can't end up with three sections reseeded from a
 * new payload and two left on the old one.
 */
export const extractAllForms = (data) => ({
  store: extractStoreForm(data?.store),
  org: extractOrgForm(data?.organization),
  company: extractCompanyForm(data?.company),
  prefs: extractPreferencesForm(data?.store_units, data?.locale),
  planning: extractPlanningForm(data?.planning),
  po: extractPOForm(data?.purchase_orders),
  exports: extractExportsForm(data?.exports),
  sourcing: extractSourcingForm(data?.sourcing),
});
